import { Box, SelectProps, SpaceBetween, TextContent } from "@amzn/awsui-components-react";
import { Button, Link } from "@amzn/uxm-aws-ui-components-react/lib/src/polaris/";
import { useRef } from "react";
import { getString } from "common/ui_string_labels/ui-strings-utils";
import CustomPopOver from "../CustomPopOver/CustomPopOver";
import CustomModal, { CustomModalRef } from "../CustomModal/CustomModal";
import { ORDER_INFORMATION_TEST_IDS } from "helpers";
import { CustomFormField } from "interfaces";
import { sanitizeWatcher, validateWatcher, validateWatcherInput } from "helpers/data-sanitization";

/**
 * Union type of all possible order information form input field names
 * @type
 */
export type OrderConfirmationFormInputs =
    | "behalfUser"
    | "costCenter"
    | "companyGLCode"
    | "locationGLCode"
    | "financeCode"
    | "project"
    | "expedite"
    | "expediteDate"
    | "deliverTo"
    | "justification"
    | "watchers"
    | "financeForm";

/**
 * Mapped type that creates an object with string IDs for each order information input
 * @type
 */
export type OrderConfirmationIds = {
    [key in OrderConfirmationFormInputs]: string;
};

/**
 * Mapped type that creates an object with CustomFormField values for each order information input
 * @type
 */
export type OrderConfirmationInputs = {
    [key in OrderConfirmationFormInputs]: CustomFormField;
};

/**
 * Props for getting order form structure configuration
 * @interface
 * @property {SelectProps.Options} financeCodes - Available finance code options for selection
 * @property {SelectProps.Options} deliverTos - Available delivery location options for selection
 * @property {() => boolean} expediteOpen - Function that returns whether expedite option is open/available for the expedite date form
 * @property {(id: string) => string | undefined} getHasError - function that checks if a parameters has an error
 * @property {() => string} onBehalf = Retrieves the onBehalf user name
 */
export interface getOrderFormStructureProps {
    financeCodes: SelectProps.Options;
    deliverTos: SelectProps.Options;
    expediteOpen: () => boolean;
    getHasError: (id: string) => string | undefined;
    onBehalf: string;
}

const getStringPath: string = "common.component.LocalItOrderConfirmationContent.orderForm";
const getStringPathLinks: string = "links";

const formStringPaths = {
    behalfUser: `${getStringPath}.behalfUser`,
    costCenter: `${getStringPath}.costCenter`,
    companyGLCode: {
        general: `${getStringPath}.companyGLCode`,
        modal: `${getStringPath}.companyGLCode.modal`,
        modalContent: `${getStringPath}.companyGLCode.modal.content`,
    },
    locationGLCode: {
        general: `${getStringPath}.locationGLCode`,
        modal: `${getStringPath}.locationGLCode.modal`,
        modalContent: `${getStringPath}.locationGLCode.modal.content`,
    },
    financeCode: `${getStringPath}.financeCode`,
    project: `${getStringPath}.project`,
    deliverTo: `${getStringPath}.deliverTo`,
    businessJustification: {
        general: `${getStringPath}.businessJustification`,
        modal: `${getStringPath}.businessJustification.modal`,
        copyButton: `${getStringPath}.businessJustification.modal.copyButton`,
    },
    watchers: {
        general: `${getStringPath}.watchers`,
    },
};

/**
 * Component that displays business justification format information and copy functionality
 *
 * @returns {JSX.Element} Modal with business justification guidelines
 */
export const BusinessJustificationModal = () => {
    const modalRef = useRef<CustomModalRef>(null);

    const textToCopy = getString(`${formStringPaths.businessJustification.modal}.format`);

    const closeModal = () => {
        modalRef.current?.close();
    };

    const onClickAction = async () => {
        try {
            await navigator.clipboard.writeText(textToCopy);
            closeModal();
        } catch (error) {
            /**
             * @todo send an error message through the flashbar notification. Implementing it in the next CR.
             */
        }
    };

    return (
        <CustomModal
            ref={modalRef}
            iconName={"status-info"}
            variant="icon"
            modalHeader={getString(`${formStringPaths.businessJustification.modal}.header`)}
            modalContent={
                <SpaceBetween size={"s"}>
                    <Box color="text-status-error">
                        {getString(`${formStringPaths.businessJustification.modal}.replaceText`)}
                    </Box>
                    <Box variant="code">{getString(`${formStringPaths.businessJustification.modal}.format`)}</Box>
                    <Link
                        href={getString(`${getStringPathLinks}.businessJustificationLink`)}
                        external
                        touchpoint="open-business-justification-modal"
                    >
                        {getString(`${formStringPaths.businessJustification.modal}.moreInformation`)}
                    </Link>
                    <Button iconName="copy" onClick={onClickAction} touchpoint="copy-justification-template">
                        {getString(`${formStringPaths.businessJustification.copyButton}.copyText`)}
                    </Button>
                </SpaceBetween>
            }
        />
    );
};

/**
 * Object containing all form field IDs for the order information form
 *
 * @type {OrderConfirmationIds}
 */
export const orderConfirmationFormIds: OrderConfirmationIds = {
    behalfUser: "behalfUser",
    costCenter: "costCenter",
    companyGLCode: "companyGLCode",
    locationGLCode: "locationGLCode",
    financeCode: "financeCode",
    project: "project",
    expedite: "expedite",
    expediteDate: "expediteDate",
    deliverTo: "deliverTo",
    justification: "justification",
    watchers: "watchers",
    financeForm: "financeForm",
};

/**
 * The default rows required for the business justification to fit the justification template
 */
const JUSTIFICATION_CONTENT_DEFAULT_ROWS = 6;

/**
 * Allowed attachment types for finance form file uploads
 */
const ALLOWED_ATTACHMENT_TYPES = [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/msword",
    "application/octet-stream",
    "application/vnd.ms-excel",
    "application/vnd.ms-outlook",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/zip",
    "image/jpeg",
    "image/png",
    "message/rfc822",
    "text/csv",
    "text/plain",
];

/**
 * Function that generates the complete form structure configuration for order information
 *
 * @param {getOrderFormStructureProps} props - Configuration props for form structure
 * @param {SelectProps.Options} props.financeCodes - Available finance code options
 * @param {SelectProps.Options} props.deliverTos - Available delivery location options
 * @param {() => boolean} props.expediteOpen - Function to check if expedite option is enabled
 * @param {string} props.onBehalf - Function that retrieves the onBehalf user
 *
 * @returns {OrderConfirmationInputs} Complete form field configuration object
 */
export const getOrderFormStructure = ({
    financeCodes,
    deliverTos,
    expediteOpen,
    getHasError,
    onBehalf,
}: getOrderFormStructureProps): OrderConfirmationInputs => {
    return {
        behalfUser: {
            id: orderConfirmationFormIds.behalfUser,
            variant: "input",
            label: getString(`${formStringPaths.behalfUser}.label`),
            placeholder: getString(`${formStringPaths.behalfUser}.placeholder`),
            info: (
                <CustomPopOver
                    content={<TextContent>{getString(`${formStringPaths.behalfUser}.description`)}</TextContent>}
                />
            ),
            errorText: getHasError(orderConfirmationFormIds.behalfUser),
            testId: ORDER_INFORMATION_TEST_IDS.ON_BEHALF,
            touchpoint: "intake-order-on-behalf-user",
        },
        costCenter: {
            id: orderConfirmationFormIds.costCenter,
            variant: "input",
            label: getString(`${formStringPaths.costCenter}.label`),
            placeholder: getString(`${formStringPaths.costCenter}.placeholder`),
            errorText: getHasError(orderConfirmationFormIds.costCenter),
            testId: ORDER_INFORMATION_TEST_IDS.COST_CENTER,
            touchpoint: "intake-order-cost-center",
        },
        companyGLCode: {
            id: orderConfirmationFormIds.companyGLCode,
            variant: "input",
            label: getString(`${formStringPaths.companyGLCode.general}.label`),
            placeholder: getString(`${formStringPaths.companyGLCode.general}.placeholder`),
            errorText: getHasError(orderConfirmationFormIds.companyGLCode),
            testId: ORDER_INFORMATION_TEST_IDS.COMPANY_GL_CODE,
            touchpoint: "intake-order-company-gl-code",
        },
        locationGLCode: {
            id: orderConfirmationFormIds.locationGLCode,
            variant: "input",
            label: getString(`${formStringPaths.locationGLCode.general}.label`),
            placeholder: getString(`${formStringPaths.locationGLCode.general}.placeholder`),
            errorText: getHasError(orderConfirmationFormIds.locationGLCode),
            testId: ORDER_INFORMATION_TEST_IDS.LOCATION_GL_CODE,
            touchpoint: "intake-order-location-gl-code",
        },
        financeCode: {
            id: orderConfirmationFormIds.financeCode,
            variant: "select",
            label: getString(`${formStringPaths.financeCode}.label`),
            placeholder: getString(`${formStringPaths.financeCode}.placeholder`),
            options: financeCodes,
            errorText: getHasError(orderConfirmationFormIds.financeCode),
            testId: ORDER_INFORMATION_TEST_IDS.FINANCE_CODE,
            touchpoint: "intake-order-finance-code",
        },
        project: {
            id: orderConfirmationFormIds.project,
            variant: "input",
            label: getString(`${formStringPaths.project}.label`),
            placeholder: getString(`${formStringPaths.project}.placeholder`),
            testId: ORDER_INFORMATION_TEST_IDS.PROJECT_CODE,
            touchpoint: "intake-order-project-code",
        },
        expedite: {
            id: orderConfirmationFormIds.expedite,
            variant: "toggle",
            label: getString(`${getStringPath}.expedite`),
            testId: ORDER_INFORMATION_TEST_IDS.EXPEDITE,
            touchpoint: "intake-order-expedite-toggle",
        },
        expediteDate: {
            id: orderConfirmationFormIds.expediteDate,
            variant: "date-picker",
            label: getString(`${getStringPath}.expediteDate`),
            isVisible: expediteOpen(),
            errorText: getHasError(orderConfirmationFormIds.expediteDate),
            testId: ORDER_INFORMATION_TEST_IDS.EXPEDITE_DATE,
            touchpoint: "intake-order-expedite-date",
        },
        deliverTo: {
            id: orderConfirmationFormIds.deliverTo,
            variant: "select",
            label: getString(`${formStringPaths.deliverTo}.label`),
            placeholder: getString(`${formStringPaths.deliverTo}.placeholder`),
            options: deliverTos,
            errorText: getHasError(orderConfirmationFormIds.deliverTo),
            testId: ORDER_INFORMATION_TEST_IDS.DELIVER_TO,
            touchpoint: "intake-order-deliver-to",
        },
        justification: {
            id: orderConfirmationFormIds.justification,
            variant: "textArea",
            label: getString(`${formStringPaths.businessJustification.general}.label`),
            placeholder: getString(`${formStringPaths.businessJustification.general}.placeholder`),
            errorText: getHasError(orderConfirmationFormIds.justification),
            info: <BusinessJustificationModal />,
            testId: ORDER_INFORMATION_TEST_IDS.BUSINESS_JUSTIFICATION,
            rows: JUSTIFICATION_CONTENT_DEFAULT_ROWS,
            touchpoint: "intake-order-justification",
        },
        watchers: {
            id: orderConfirmationFormIds.watchers,
            variant: "array-input",
            label: getString(`${formStringPaths.watchers.general}.label`),
            placeholder: getString(`${formStringPaths.watchers.general}.placeholder`),
            info: (
                <CustomPopOver
                    content={<TextContent>{getString(`${formStringPaths.watchers.general}.description`)}</TextContent>}
                />
            ),
            testId: ORDER_INFORMATION_TEST_IDS.WATCHERS,
            touchpoint: "intake-order-watchers",
            sanitizeInput: sanitizeWatcher,
            validateInput: validateWatcherInput,
        },
        financeForm: {
            id: orderConfirmationFormIds.financeForm,
            variant: "fileDropzone",
            errorText: getHasError(orderConfirmationFormIds.financeForm),
            testId: ORDER_INFORMATION_TEST_IDS.ATTACHMENT,
            accept: ALLOWED_ATTACHMENT_TYPES.join(","),
            multiple: true,
            touchpoint: "intake-order-finance-form",
        },
    };
};
